import { useEffect, useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import GridTexture from "../components/GridTexture";

import r1 from "../assets/cover/review/nx11.webp";
import r2 from "../assets/cover/review/nx12.webp";
import r3 from "../assets/cover/review/nx3.webp";
import r4 from "../assets/cover/review/nx4.webp";
import r5 from "../assets/cover/review/nx5.webp";
import r6 from "../assets/cover/review/nx6.webp";
import r7 from "../assets/cover/review/nx7.webp";
import r8 from "../assets/cover/review/nx8.webp";
import r9 from "../assets/cover/review/nx3.webp";
import r10 from "../assets/cover/review/nx4.webp";

const REVIEWS = [
  {
    name: "Jan van de Berg",
    country: "NL",
    img: r1,
    text: "Geweldige service! De beeldkwaliteit is haarscherp (4K) en de Eredivisie-wedstrijden bufferen nooit. Binnen 5 minuten na betaling had ik mijn gegevens via WhatsApp.",
  },
  {
    name: "Sanne De Smet",
    country: "BE",
    img: r2,
    text: "Eindelijk alle Belgische en Nederlandse zenders in één pakket, inclusief alle sportkanalen en de nieuwste films. De helpdesk hielp me direct met installeren op mijn Samsung TV.",
  },
  {
    name: "Mark Vermeulen",
    country: "NL",
    img: r3,
    text: "Al 6 maanden abonnee van het Lifetime-pakket. Geen enkele storing gehad tijdens de Champions League. Echt een aanrader!",
  },
  {
    name: "Anouk Janssens",
    country: "NL",
    img: r4,
    text: "De VOD-sectie is gigantisch. Alles met Nederlandse ondertiteling en prima kwaliteit. Mijn hele gezin is er super blij mee.",
  },
  {
    name: "Kevin Maes",
    country: "BE",
    img: r5,
    text: "Had een klein vraagje over de installatie op Smart IPTV en werd via WhatsApp binnen 2 minuten geholpen. Top service!",
  },
  {
    name: "Dennis Mulder",
    country: "NL",
    img: r6,
    text: "Bespaart me honderden euro's per jaar vergeleken met traditionele providers. Alles werkt vlekkeloos op mijn Firestick.",
  },
  {
    name: "Lotte Bakker",
    country: "NL",
    img: r7,
    text: "Snel geïnstalleerd op de Apple TV en meteen alle Eredivisie-wedstrijden in HD. Geen gedoe, gewoon kijken.",
  },
  {
    name: "Thomas Peeters",
    country: "BE",
    img: r8,
    text: "Beeld en geluid perfect, ook tijdens drukke speelrondes. De prijs-kwaliteitverhouding is uitstekend.",
  },
  {
    name: "Rick de Groot",
    country: "NL",
    img: r9,
    text: "De IBO Pro Player-app draaide meteen na het invoeren van de gegevens. Overzichtelijk menu en alles werkt vloeiend op mijn Android TV-box.",
  },
  {
    name: "Emma Willems",
    country: "BE",
    img: r10,
    text: "Setup was zo gebeurd: playlist toegevoegd en klaar. Live TV, films en series allemaal netjes gesorteerd. Precies wat ik zocht.",
  },
];

export default function Review() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const loop = [...REVIEWS, ...REVIEWS];

  const getStep = () => {
    const el = trackRef.current;
    if (!el) return 1;
    const card = el.querySelector("[data-card]");
    return card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
  };

  const scrollByCard = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * getStep(), behavior: "smooth" });
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    // Slide-by-slide autoplay — never pauses, regardless of hover/click/touch.
    const advance = () => {
      const half = el.scrollWidth / 2;
      // Naadloze lus: als we in de identieke tweede set zitten, spring
      // onzichtbaar terug (zelfde kaart) en schuif daarna verder.
      if (half > 0 && el.scrollLeft >= half - 1) el.scrollLeft -= half;
      el.scrollBy({ left: getStep(), behavior: "smooth" });
    };
    const id = setInterval(advance, 1800);

    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / getStep());
      setActive(((idx % REVIEWS.length) + REVIEWS.length) % REVIEWS.length);
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearInterval(id);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <section
      className="py-14 sm:py-20 bg-surface-primary relative overflow-hidden"
      id="reviews"
      style={{ "--section-bg": "#000000" }}
    >
      <GridTexture />
      <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto relative">
        <SectionHeading
          kicker="4.9 / 5 — 12.000+ beoordelingen"
          title={
            <>
              Wat Onze{" "}
              <span className="text-transparent bg-clip-text bg-gradient-text">
                Klanten Zeggen
              </span>
            </>
          }
          subtitle="Echte reacties van echte klanten in Nederland en België"
        />

        {/* Carousel */}
        <div className="relative">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-surface-card shadow hover:shadow-glow-cyan-sm border border-line hover:border-accent/40 text-ink-secondary hover:text-accent transition"
            aria-label="Vorige beoordelingen"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-surface-card shadow hover:shadow-glow-cyan-sm border border-line hover:border-accent/40 text-ink-secondary hover:text-accent transition"
            aria-label="Volgende beoordelingen"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            ref={trackRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-2 scrollbar-hide"
          >
            {loop.map((review, index) => (
              <div
                key={index}
                data-card
                className="flex-none w-[85%] sm:w-[70%] md:w-1/2 lg:w-1/3 rounded-xl sm:rounded-2xl bg-gradient-button p-[3px] shadow-md hover:shadow-glow-cyan-sm transition-all duration-300"
              >
              <div className="bg-surface-card rounded-[calc(0.75rem-3px)] sm:rounded-[calc(1rem-3px)] p-4 sm:p-5 h-full">
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="overflow-hidden rounded-lg sm:rounded-xl bg-white/5 h-36 sm:h-44 md:h-56">
                    <img
                      src={review.img}
                      alt={`Beoordeling van ${review.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-accent mb-2">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} className="w-4 h-4 fill-accent" />
                      ))}
                    </div>
                    <p className="text-ink-secondary leading-relaxed text-sm sm:text-base">
                      {review.text}
                    </p>
                    <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                      <span className="inline-flex items-center justify-center flex-shrink-0 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-button text-white text-xs font-bold">
                        {review.name.charAt(0)}
                      </span>
                      <div>
                        <p className="text-white font-semibold text-xs sm:text-sm">
                          {review.name}
                        </p>
                        <p className="text-ink-muted text-xs">{review.country}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {REVIEWS.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition ${
                  active === i ? "bg-accent" : "bg-white/15"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
